import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { Star, MapPin } from 'lucide-react'
import type { InstructorWithProfile } from '@/types/database'

// Lazy-import leaflet only on client to avoid SSR issues
let L: typeof import('leaflet') | null = null

interface GeoPoint { lat: number; lng: number }
interface GeoCache { [city: string]: GeoPoint | null }

const geocacheRef: GeoCache = {}

async function geocodeCity(city: string): Promise<GeoPoint | null> {
  if (city in geocacheRef) return geocacheRef[city]
  try {
    const res = await fetch(
      `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(city + ', Brazil')}&format=json&limit=1`,
      { headers: { 'Accept-Language': 'pt-BR' } },
    )
    const data = await res.json()
    if (data?.[0]) {
      const pt = { lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon) }
      geocacheRef[city] = pt
      return pt
    }
  } catch {
    // ignore
  }
  geocacheRef[city] = null
  return null
}

function formatCurrency(value: number | null): string {
  if (value === null) return 'Consultar'
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 0 }).format(value)
}

interface Props {
  instructors: InstructorWithProfile[]
}

export function InstructorMapView({ instructors }: Props) {
  const mapRef = useRef<HTMLDivElement>(null)
  const leafletMap = useRef<any>(null)
  const markersRef = useRef<any[]>([])
  const [geocoded, setGeocoded] = useState<Map<string, GeoPoint>>(new Map())

  // Bootstrap leaflet CSS once
  useEffect(() => {
    if (!document.getElementById('leaflet-css')) {
      const link = document.createElement('link')
      link.id = 'leaflet-css'
      link.rel = 'stylesheet'
      link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css'
      document.head.appendChild(link)
    }
  }, [])

  // Init map
  useEffect(() => {
    if (!mapRef.current) return
    let destroyed = false

    async function init() {
      L = await import('leaflet')
      if (destroyed || !mapRef.current) return
      if (leafletMap.current) return

      const map = L.map(mapRef.current).setView([-15.7801, -47.9292], 4)
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© OpenStreetMap contributors',
        maxZoom: 18,
      }).addTo(map)
      leafletMap.current = map
    }

    init()
    return () => {
      destroyed = true
      if (leafletMap.current) {
        leafletMap.current.remove()
        leafletMap.current = null
      }
    }
  }, [])

  // Geocode all unique cities
  useEffect(() => {
    const cities = new Set<string>()
    for (const inst of instructors) {
      const city = (inst.profile as any)?.city
      if (city) cities.add(city)
    }
    Promise.all(
      Array.from(cities).map(async (city) => {
        const pt = await geocodeCity(city)
        return { city, pt }
      }),
    ).then((results) => {
      setGeocoded((prev) => {
        const next = new Map(prev)
        for (const { city, pt } of results) {
          if (pt) next.set(city, pt)
        }
        return next
      })
    })
  }, [instructors])

  // Place markers
  useEffect(() => {
    if (!leafletMap.current || !L) return

    // Clear old markers
    for (const m of markersRef.current) m.remove()
    markersRef.current = []

    for (const inst of instructors) {
      const city = (inst.profile as any)?.city
      const pt = city ? geocoded.get(city) : undefined
      if (!pt) continue

      const icon = L!.divIcon({
        className: '',
        iconSize: [38, 38],
        iconAnchor: [19, 38],
        popupAnchor: [0, -40],
        html: `
          <div style="
            width:38px;height:38px;border-radius:50% 50% 50% 0;
            background:linear-gradient(135deg,#0054b3,#0891b2);
            transform:rotate(-45deg);
            box-shadow:0 2px 8px rgba(0,84,179,.4);
            border:2px solid white;
          ">
            <svg style="transform:rotate(45deg);position:absolute;top:7px;left:7px" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
            </svg>
          </div>`,
      })

      const profilePath = inst.slug ? `/instrutores/${inst.slug}` : '#'
      const name = (inst.profile as any)?.full_name ?? 'Instrutor'
      const rating = inst.rating_avg != null ? inst.rating_avg.toFixed(1) : '—'
      const price = formatCurrency(inst.hourly_rate)

      const popup = L!.popup({ maxWidth: 220, className: 'instructor-popup' }).setContent(`
        <div style="font-family:system-ui,sans-serif;padding:4px 0">
          <p style="font-weight:700;font-size:14px;margin:0 0 4px;color:#111">${name}</p>
          <p style="font-size:12px;color:#6b7280;margin:0 0 2px">📍 ${city}</p>
          <div style="display:flex;align-items:center;gap:8px;margin:6px 0">
            <span style="font-size:12px;color:#374151">⭐ ${rating}</span>
            <span style="font-size:13px;font-weight:700;color:#0054b3">${price}/h</span>
          </div>
          <a href="${profilePath}" style="display:block;text-align:center;background:#0054b3;color:white;border-radius:8px;padding:6px 12px;font-size:12px;font-weight:600;text-decoration:none;margin-top:6px">
            Ver perfil →
          </a>
        </div>
      `)

      const marker = L!.marker([pt.lat, pt.lng], { icon }).bindPopup(popup).addTo(leafletMap.current)
      markersRef.current.push(marker)
    }

    // Fit bounds if we have markers
    if (markersRef.current.length > 0) {
      const group = L!.featureGroup(markersRef.current)
      leafletMap.current.fitBounds(group.getBounds().pad(0.2))
    }
  }, [instructors, geocoded])

  return (
    <div className="relative w-full rounded-2xl overflow-hidden border border-gray-200 shadow-sm" style={{ height: 540 }}>
      <div ref={mapRef} className="w-full h-full" />
      {instructors.length === 0 && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-white/80 backdrop-blur-sm">
          <MapPin className="h-8 w-8 text-gray-300 mb-2" />
          <p className="text-sm text-muted-foreground">Nenhum instrutor para exibir no mapa</p>
        </div>
      )}
      <div className="absolute bottom-3 left-3 bg-white/90 backdrop-blur-sm text-xs text-gray-500 px-2.5 py-1 rounded-full shadow-sm border border-gray-100">
        {instructors.length} instrutor{instructors.length !== 1 ? 'es' : ''} no mapa
      </div>
    </div>
  )
}
